import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, RefreshCw, WifiOff, X } from 'lucide-react';
import { getOfflineAudioManifest } from '../../services/offlineAudioService';
import { activateWaitingServiceWorker, refreshServiceWorkerRegistration } from '../../utils/registerServiceWorker';

const INSTALL_DISMISSED_KEY = 'eduvibz.pwaInstallDismissedAt';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isStandaloneDisplay = () => (
  window.matchMedia?.('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

const getAndroidInstallCompatibility = () => {
  const userAgent = window.navigator.userAgent || '';
  const isAndroid = /Android/i.test(userAgent);
  const isSamsungInternet = /SamsungBrowser/i.test(userAgent);

  return {
    isAndroid,
    isSamsungInternet,
    canUseNativePrompt: !isAndroid || !isSamsungInternet
  };
};

const wasRecentlyDismissed = () => {
  const dismissedAt = Number(window.localStorage?.getItem(INSTALL_DISMISSED_KEY) || 0);
  return dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS;
};

const getOfflineSongIdFromPath = (path = '') => (
  path.match(/^\/music-player\/song\/([^/]+)/)?.[1]
);

const isSavedOfflineSongPath = (path = '') => {
  const songId = getOfflineSongIdFromPath(path);
  if (!songId) return false;
  return Boolean(getOfflineAudioManifest()[songId]);
};

const PwaStatusPrompt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installGuidance, setInstallGuidance] = useState(null);
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isDismissed, setIsDismissed] = useState(() => wasRecentlyDismissed());
  const [connectivityNoticeMode, setConnectivityNoticeMode] = useState('expanded');
  const isOfflineLibrary = location.pathname === '/offline';
  const isOfflinePlayer = isSavedOfflineSongPath(location.pathname);
  const isIntentionalOfflineExperience = isOfflineLibrary || isOfflinePlayer;

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      if (!isStandaloneDisplay() && !wasRecentlyDismissed()) {
        const compatibility = getAndroidInstallCompatibility();
        if (compatibility.canUseNativePrompt) {
          setInstallPrompt(event);
          setInstallGuidance(null);
        } else {
          setInstallPrompt(null);
          setInstallGuidance('chrome');
        }
        setIsDismissed(false);
      }
    };

    const handleUpdateReady = (event) => {
      setUpdateRegistration(event.detail?.registration || null);
    };

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('eduvibz:pwa-update-ready', handleUpdateReady);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('eduvibz:pwa-update-ready', handleUpdateReady);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOfflinePlayer) {
      setConnectivityNoticeMode('hidden');
      return;
    }

    setConnectivityNoticeMode(isOfflineLibrary ? 'collapsed' : 'expanded');
  }, [isOfflinePlayer, isOfflineLibrary, isOnline, location.pathname]);

  const dismissInstall = () => {
    window.localStorage?.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
    setIsDismissed(true);
    setInstallPrompt(null);
    setInstallGuidance(null);
  };

  const installApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const applyUpdate = async () => {
    setUpdateRegistration(null);
    const activated = await activateWaitingServiceWorker(updateRegistration);
    if (activated) {
      window.location.reload();
      return;
    }

    await refreshServiceWorkerRegistration(updateRegistration);
    window.location.reload();
  };

  const renderConnectivityChip = ({ label, Icon, onClick, tone = 'offline' }) => (
    <button
      type="button"
      onClick={onClick}
      className={`fixed right-4 top-24 z-[80] flex h-12 w-12 items-center justify-center rounded-full border shadow-2xl backdrop-blur transition hover:scale-105 sm:right-6 ${
        tone === 'online'
          ? 'border-eduvibe-green/40 bg-eduvibe-green/15 text-eduvibe-green'
          : 'border-eduvibe-blue/40 bg-eduvibe-dark/92 text-eduvibe-blue'
      }`}
      aria-label={label}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </button>
  );

  if (!isOnline) {
    if (connectivityNoticeMode === 'hidden') return null;
    if (connectivityNoticeMode === 'collapsed' || isIntentionalOfflineExperience) {
      return renderConnectivityChip({
        label: 'Show offline status',
        Icon: WifiOff,
        onClick: () => setConnectivityNoticeMode('expanded')
      });
    }

    return (
      <div className="fixed bottom-4 left-4 right-4 z-[80] mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-eduvibe-blue/40 bg-eduvibe-dark/95 px-4 py-4 text-white shadow-2xl backdrop-blur sm:flex-row sm:items-center">
        <WifiOff className="h-5 w-5 shrink-0 text-eduvibe-blue" aria-hidden="true" />
        <p className="flex-1 text-sm font-medium">You are offline. Downloaded songs can still play on this device.</p>
        <button
          type="button"
          onClick={() => {
            navigate('/offline', { state: { from: location.pathname } });
            setConnectivityNoticeMode('hidden');
          }}
          className="rounded-full bg-eduvibe-green px-4 py-2 text-sm font-black text-eduvibe-dark"
        >
          Downloaded songs
        </button>
        <button
          type="button"
          onClick={() => setConnectivityNoticeMode('hidden')}
          className="absolute right-3 top-3 rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white sm:static"
          aria-label="Dismiss offline status"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (location.pathname === '/offline') {
    if (connectivityNoticeMode === 'hidden') return null;
    if (connectivityNoticeMode === 'collapsed') {
      return renderConnectivityChip({
        label: 'Show online status',
        Icon: RefreshCw,
        onClick: () => setConnectivityNoticeMode('expanded'),
        tone: 'online'
      });
    }

    return (
      <div className="fixed bottom-4 left-4 right-4 z-[80] mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-eduvibe-green/40 bg-eduvibe-dark/95 px-4 py-4 text-white shadow-2xl backdrop-blur sm:flex-row sm:items-center">
        <RefreshCw className="h-5 w-5 shrink-0 text-eduvibe-green" aria-hidden="true" />
        <p className="flex-1 text-sm font-medium">You are back online. Fresh tracks and playlists are available again.</p>
        <button
          type="button"
          onClick={() => {
            navigate('/browse');
            setConnectivityNoticeMode('hidden');
          }}
          className="rounded-full bg-eduvibe-green px-4 py-2 text-sm font-black text-eduvibe-dark"
        >
          Browse music
        </button>
        <button
          type="button"
          onClick={() => setConnectivityNoticeMode('hidden')}
          className="absolute right-3 top-3 rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white sm:static"
          aria-label="Dismiss online status"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (updateRegistration) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-[80] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-eduvibe-green/40 bg-eduvibe-dark/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
        <RefreshCw className="h-5 w-5 shrink-0 text-eduvibe-green" aria-hidden="true" />
        <p className="flex-1 text-sm font-medium">A fresher EduVibz experience is ready.</p>
        <button
          type="button"
          onClick={applyUpdate}
          className="rounded-full bg-eduvibe-green px-4 py-2 text-sm font-black text-eduvibe-dark"
        >
          Update
        </button>
        <button
          type="button"
          onClick={() => setUpdateRegistration(null)}
          className="rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
          aria-label="Dismiss update prompt"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  if ((!installPrompt && !installGuidance) || isDismissed) return null;

  if (installGuidance === 'chrome') {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-[80] mx-auto flex max-w-md items-center gap-3 rounded-lg border border-eduvibe-green/40 bg-eduvibe-dark/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
        <Download className="h-5 w-5 shrink-0 text-eduvibe-green" aria-hidden="true" />
        <p className="flex-1 text-sm font-medium">For a safe Android install, open EduVibz in Chrome and install it from there.</p>
        <button
          type="button"
          onClick={dismissInstall}
          className="rounded-full p-1 text-eduvibe-light transition hover:text-white"
          aria-label="Dismiss install guidance"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[80] mx-auto flex max-w-md items-center gap-3 rounded-lg border border-eduvibe-green/40 bg-eduvibe-dark/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
      <Download className="h-5 w-5 shrink-0 text-eduvibe-green" aria-hidden="true" />
      <p className="flex-1 text-sm font-medium">Install EduVibz for a faster app-like study music experience.</p>
      <button
        type="button"
        onClick={installApp}
        className="rounded-md bg-eduvibe-green px-3 py-2 text-sm font-bold text-eduvibe-dark"
      >
        Install
      </button>
      <button
        type="button"
        onClick={dismissInstall}
        className="rounded-full p-1 text-eduvibe-light transition hover:text-white"
        aria-label="Dismiss install prompt"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default PwaStatusPrompt;
